/** @see {@link apollo_platform/apps/invitations/api/serializers.py} > CustomerRelationInvitationSerializer */
export interface InvitationCustomer {
  id: number;
  name: string;
  debtor_number: string;
  role: string | null;
}

export interface Invitation {
  id: number;
  email: string;
  created: string;
  sent: string | null;
  accepted: boolean;
  accepted_at: string | null;
  inviter: string;
  customers: InvitationCustomer[];
  role_invitations: Role[];
  url: string;
  key_expired: boolean;
}
export type RoleCreate = {
  id: number;
  name: string;
};
export type InvitationCreate = {
  email: string;
  roles: RoleCreate[];
  standalone_email_otp?: boolean;
};

export const useInvitationsStore = defineStore('invitations', () => {
  const invitations = ref<Invitation[] | null>(null);

  const getInvitations = computed<Invitation[]>(() => invitations.value || []);
  const getUserInvitations = computed(
    () => (email: string) => getInvitations.value.filter((i) => i?.email === email),
  );

  const setInvitations = (value: Invitation[]) => {
    useLocalStorage().setItem('invitations', value);
    invitations.value = value;
  };

  const createInvitation = async (invitation: InvitationCreate) => {
    try {
      const {data} = await apiPostInvitation(invitation);
      return data;
    } catch (error) {
      throw apiErrorMessage(error, true);
    }
  };

  const readInvitations = async () => {
    try {
      const {data} = await apiGetInvitations(); // This endpoint uses cursor pagination
      // Get the next cursor data
      const aggregatedInvitationResults: Invitation[] =
        data.next !== null ? await apiGetPaginatedResults(apiGetInvitations, data.next) : [];
      // Return both the first cursor & rest of the aggregated cursors.
      const results: Invitation[] = [...data.results, ...aggregatedInvitationResults];
      setInvitations(results);
      return results;
    } catch (error) {
      throw apiErrorMessage(error);
    }
  };

  const updateInvitation = async <T extends InvitationAction>(
    id: number,
    action: T,
    data: DataOfAction<T>,
  ): Promise<Invitation | undefined> => {
    try {
      const response = await apiPutInvitation(id, action, data);
      return response.data;
    } catch (error) {
      throw apiErrorMessage(error);
    }
  };

  return {
    invitations,
    getInvitations,
    getUserInvitations,
    setInvitations,
    createInvitation,
    readInvitations,
    updateInvitation,
  };
});
